import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import { categories, videos } from '@/mocks/videos';

const categoryNameMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  dongman: '动漫',
  jilupian: '纪录片',
};

const categoryDescriptions: Record<string, string> = {
  dianying: '最新热门电影推荐，涵盖科幻、动作、喜剧、悬疑等多种类型，高清免费在线观看。51视频聚合香蕉视频、红桃视频、91视频等平台海量电影资源，不卡顿不缓冲。',
  dianshiju: '热门电视剧全集免费在线观看，国产剧、海外剧应有尽有。51视频提供流畅高清的追剧体验，聚合香蕉视频、红桃视频、91视频最新剧集。',
  zongyi: '最新综艺节目免费在线观看，音乐竞演、真人秀、脱口秀等多种类型。51视频聚合热门综艺，高清不卡顿不缓冲，精彩不停。',
  dongman: '热门动漫免费在线观看，国产动漫、日漫新番一网打尽。51视频提供高清流畅的动漫观看体验，追番首选平台。',
  jilupian: '精选纪录片免费在线观看，自然探索、人文历史、美食旅行等多种主题。51视频聚合优质纪录片资源，高清画质开阔视野。',
};

export default function CategoryPage() {
  const params = useParams();
  const { slug } = params;
  const [activeSlug, setActiveSlug] = useState(slug || '');
  const [sortBy, setSortBy] = useState('default');
  const [regionFilter, setRegionFilter] = useState('all');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = currentCategory?.name || '全部';
  const catDesc = categoryDescriptions[activeSlug] || '';

  let filteredVideos = activeSlug ? videos.filter((v) => v.category === activeSlug) : videos;

  const regions = [...new Set(filteredVideos.map((v) => v.region))];

  if (regionFilter !== 'all') {
    filteredVideos = filteredVideos.filter((v) => v.region === regionFilter);
  }

  if (sortBy === 'rating') {
    filteredVideos = [...filteredVideos].sort((a, b) => b.rating - a.rating);
  } else if (sortBy === 'newest') {
    filteredVideos = [...filteredVideos].sort((a, b) => b.year - a.year);
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName },
  ];

  return (
    <>
      <SEOHead
        title={`${categoryName} - 51视频 | ${categoryName}免费在线观看 | 高清不卡顿`}
        description={catDesc || `51视频${categoryName}频道，聚合香蕉视频、红桃视频、91视频等平台最新${categoryName}资源，高清免费在线观看，不卡顿不缓冲。`}
        keywords={`51视频,${categoryName},免费在线观看,高清,不卡顿,香蕉视频,红桃视频,91视频`}
        canonicalUrl={`https://51video.example.com/category/${activeSlug}`}
      />
      <SchemaOrg type="WebPage" data={{
        name: `${categoryName} - 51视频`,
        description: catDesc,
        url: `https://51video.example.com/category/${activeSlug}`,
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51video.example.com/' },
          { '@type': 'ListItem', position: 2, name: categoryName, item: `https://51video.example.com/category/${activeSlug}` },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mb-8">
                <CategoryTabs categories={categories} activeSlug={activeSlug} />
              </div>

              {catDesc && (
                <p className="text-sm text-foreground-500 mb-6 leading-relaxed">{catDesc}</p>
              )}

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-8 flex-wrap">
                <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
                  {[
                    { value: 'default', label: '默认排序' },
                    { value: 'rating', label: '评分最高' },
                    { value: 'newest', label: '最新发布' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSortBy(opt.value)}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                        sortBy === opt.value
                          ? 'bg-primary-500 text-white'
                          : 'text-foreground-600 hover:text-primary-500'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {regions.length > 0 && (
                  <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
                    <button
                      onClick={() => setRegionFilter('all')}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                        regionFilter === 'all'
                          ? 'bg-secondary-500 text-white'
                          : 'text-foreground-600 hover:text-secondary-500'
                      }`}
                    >
                      全部地区
                    </button>
                    {regions.map((r) => (
                      <button
                        key={r}
                        onClick={() => setRegionFilter(r)}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                          regionFilter === r
                            ? 'bg-secondary-500 text-white'
                            : 'text-foreground-600 hover:text-secondary-500'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {filteredVideos.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-5xl text-foreground-300"></i>
                  <p className="mt-4 text-foreground-500">暂无相关影视内容</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-14">
                  {filteredVideos.map((video) => (
                    <VideoCard
                      key={video.id}
                      id={video.id}
                      title={video.title}
                      cover={video.cover}
                      rating={video.rating}
                      category={video.category}
                      updateInfo={video.updateInfo}
                      categoryName={categoryNameMap[video.category] || video.category}
                      isHot={video.isHot}
                      isNew={video.isNew}
                    />
                  ))}
                </div>
              )}

              {filteredVideos.length > 10 && (
                <div className="flex items-center justify-center gap-3 pb-14">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-white font-medium text-sm cursor-pointer whitespace-nowrap">1</button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 font-medium text-sm cursor-pointer whitespace-nowrap">2</button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 font-medium text-sm cursor-pointer whitespace-nowrap">3</button>
                  <span className="text-foreground-400 text-sm">...</span>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 font-medium cursor-pointer whitespace-nowrap">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}